/**
 * One match, read rather than scored.
 *
 * This is **not** the public page. That one is server-rendered HTML with zero client React
 * and its own CSP (ADR-0007), built for a stranger opening a WhatsApp link. This is the
 * in-app view for someone signed in: the same scorecard, plus the over-by-over and the
 * partnerships, and a way back to scoring it.
 *
 * ## Why this screen replays instead of reading a summary
 *
 * The matches list renders `summaryLine` exactly as the server sent it, because a 50-match
 * list would otherwise replay ~12,500 events to draw one screen (ADR-0002). A scorecard is a
 * different question. Batting figures, bowling figures, partnerships and fall of wickets are
 * all outputs of the fold, and ADR-0002's other half is that a summary may never be an
 * *input* to a calculation — so assembling a card from stored numbers is precisely the thing
 * it forbids. One match's ledger is a few hundred events and single-digit milliseconds; the
 * arithmetic ADR-0002 was worried about is the list, and the list still does it right.
 *
 * Everything below the hero is the components the scoring screen already uses, unchanged:
 * `Scorecard` for the cards, `OverStrip` for the chips. One spelling of a scorecard, three
 * surfaces (here, the pad, the public page) — which is the whole reason the formatters live
 * in the engine.
 */
import { useEffect, useMemo, useState } from "react";
import {
  formatOvers,
  formatScore,
  generateMatchSummary,
  replay,
} from "@sportsos/engine";
import type {
  GetEventsResponse,
  InningsState,
  Match,
  MatchId,
  MatchState,
  Player,
  PlayerId,
  TeamId,
} from "@sportsos/engine";
import { api, ApiRequestError, publicMatchPath } from "../api.ts";
import { mergeLedger } from "../cache/mergeLedger.ts";
import { eventStore } from "../sync/drainer.ts";
import { canOffer, isSubscribedHere, subscribe, unsubscribe } from "../notifications.ts";
import { AddPlayerSheet } from "./AddPlayerSheet.tsx";
import { Scorecard } from "./Scorecard.tsx";
import { OverStrip } from "./OverStrip.tsx";
import { WagonWheelCard } from "./WagonWheelCard.tsx";

interface Props {
  readonly matchId: string;
  readonly userId: string | null;
  readonly onBack: () => void;
  readonly onResume: (() => void) | null;
  /**
   * This phone already holds this match — its setup and its balls — but is scoring another
   * one right now. Offers the same "Resume scoring", for a live match only; it is how the
   * second match on a phone gets back to the pad instead of being re-created from scratch.
   */
  readonly onResumeHere?: (() => void) | null;
  readonly onStartScoring: ((matchId: string, toss?: { winner: string; decision: "bat" | "bowl" }) => void) | null;
}

/** Both squads as one lookup, so the card can name anyone who appears in the ledger. */
function squadLookup(match: Match): (playerId: PlayerId) => string {
  const names = new Map<string, string>();
  for (const team of [match.teamA, match.teamB]) {
    for (const player of team.squad as readonly Player[]) names.set(player.id, player.displayName);
  }
  // The id itself when a name is missing — a player who was in the ledger but not in the
  // squad the server returned. Rare, and showing the raw id is how it gets reported rather
  // than silently becoming a blank column.
  return (playerId) => names.get(playerId) ?? String(playerId);
}

/** `Tigers` → `TIG`. No logos, ever: an external image is a request this app cannot make. */
function initials(name: string): string {
  const words = name.trim().split(/\s+/).filter((word) => word !== "");
  if (words.length === 0) return "?";
  if (words.length === 1) return (words[0] ?? "").slice(0, 3).toUpperCase();
  return words.map((word) => word[0] ?? "").join("").slice(0, 3).toUpperCase();
}

type Loaded =
  | { readonly kind: "loading" }
  | { readonly kind: "failed"; readonly message: string }
  | { readonly kind: "ready"; readonly match: Match; readonly state: MatchState };

export function MatchDetail({
  matchId,
  userId,
  onBack,
  onResume,
  onResumeHere = null,
  onStartScoring,
}: Props): React.ReactElement {
  const [loaded, setLoaded] = useState<Loaded>({ kind: "loading" });
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    let live = true;
    void (async () => {
      try {
        const [match, ledger]: readonly [Match, GetEventsResponse] = await Promise.all([
          api.getMatch(matchId),
          api.getEvents(matchId),
        ]);
        // For a match scored on this phone, the balls not yet sent are part of the scoreboard.
        // Without them a saved copy of the server's ledger — or even a live one a moment
        // before the last ball reached it — shows a score behind the device holding the real one.
        const onDevice = await eventStore.getAll(matchId as MatchId).catch(() => []);
        if (!live) return;
        setLoaded({
          kind: "ready",
          match,
          state: replay(mergeLedger(ledger.events, onDevice), ledger.ruleSet, ledger.matchId),
        });
      } catch (error: unknown) {
        if (!live) return;
        setLoaded({
          kind: "failed",
          message:
            error instanceof ApiRequestError && error.status === 404
              ? "That match is not there. It may have been opened from an old link."
              : "Could not load this match. Check your connection and try again.",
        });
      }
    })();
    return () => {
      live = false;
    };
  }, [matchId, refreshKey]);

  return (
    <div className="app-page">
      <header className="ds-appbar">
        <button className="ds-iconbtn" onClick={onBack} aria-label="Back">
          <svg viewBox="0 0 24 24"><path d="m14 6-6 6 6 6"/></svg>
        </button>
        <span className="ds-appbar__body">
          <b className="ds-appbar__title">Match</b>
        </span>
      </header>

      {loaded.kind === "loading" ? <div className="ds-caption">Loading this match…</div> : null}
      {loaded.kind === "failed" ? <div className="ds-hint ds-hint--error">{loaded.message}</div> : null}
      {loaded.kind === "ready" ? (
        <LoadedMatch
          match={loaded.match}
          state={loaded.state}
          onResume={onResume ?? (loaded.match.status !== "completed" ? onResumeHere : null)}
          onStartScoring={
            userId !== null && loaded.match.createdByUserId === userId
              ? onStartScoring
              : null
          }
          onRefresh={() => setRefreshKey((k) => k + 1)}
          canChangeVisibility={userId !== null && loaded.match.createdByUserId === userId}
        />
      ) : null}
    </div>
  );
}

function LoadedMatch({
  match,
  state,
  onResume,
  onStartScoring,
  onRefresh,
  canChangeVisibility,
}: {
  readonly match: Match;
  readonly state: MatchState;
  readonly onResume: (() => void) | null;
  readonly onStartScoring: ((matchId: string, toss?: { winner: string; decision: "bat" | "bowl" }) => void) | null;
  readonly onRefresh: () => void;
  readonly canChangeVisibility: boolean;
}): React.ReactElement {
  const name = useMemo(() => squadLookup(match), [match]);
  const isPublic = match.public !== false;
  const [savingVisibility, setSavingVisibility] = useState(false);
  const [visibilityError, setVisibilityError] = useState<string | null>(null);

  const changeVisibility = async (next: boolean): Promise<void> => {
    setSavingVisibility(true);
    setVisibilityError(null);
    try {
      await api.setMatchVisibility(match.id, next);
      onRefresh();
    } catch {
      setVisibilityError("Could not change this. Check your connection and try again.");
    } finally {
      setSavingVisibility(false);
    }
  };
  const teamName = (teamId: TeamId): string =>
    teamId === match.teamA.id ? match.teamA.name : match.teamB.name;
  const live = match.status !== "completed";
  const path = publicMatchPath(match.shortcode);
  const required = match.ruleSet.playersPerSide;
  const teamACount = (match.teamA.squad as readonly Player[]).length;
  const teamBCount = (match.teamB.squad as readonly Player[]).length;
  const teamAShort = teamACount < required;
  const teamBShort = teamBCount < required;
  const squadsReady = !teamAShort && !teamBShort;
  const [addPlayerForTeam, setAddPlayerForTeam] = useState<{ id: string; name: string; index: 0 | 1 } | null>(null);
  const [showToss, setShowToss] = useState(false);
  const [tossWinner, setTossWinner] = useState<string>(match.teamA.id);
  const [tossDecision, setTossDecision] = useState<"bat" | "bowl">("bat");
  const [startingMatch, setStartingMatch] = useState(false);
  const notStarted = state.innings.length === 0;
  const [coin, setCoin] = useState<{ face: "heads" | "tails"; spinning: boolean } | null>(null);

  const flipCoin = (): void => {
    const face = Math.random() < 0.5 ? "heads" as const : "tails" as const;
    setCoin({ face, spinning: true });
    window.setTimeout(() => {
      setCoin({ face, spinning: false });
      setTossWinner(face === "heads" ? match.teamA.id : match.teamB.id);
    }, 900);
  };

  const coinInitials = (teamName: string): string => {
    const words = teamName.trim().split(/\s+/).filter((w) => w !== "");
    if (words.length === 0) return "?";
    if (words.length === 1) return (words[0] ?? "").slice(0, 2).toUpperCase();
    return words.slice(0, 2).map((w) => (w[0] ?? "").toUpperCase()).join("");
  };


  return (
    <>
      {/* Hero card */}
      <article className="ds-card" style={{ padding: "var(--ds-space-5)" }}>
        <div className="row" style={{ gap: "var(--ds-space-3)", marginBottom: "var(--ds-space-4)" }}>
          <span className="ds-avatar ds-avatar--amber">{initials(match.teamA.name)}</span>
          <span style={{ fontSize: "var(--ds-text-sm)", fontWeight: 700, color: "var(--ds-text-muted)" }}>v</span>
          <span className="ds-avatar ds-avatar--teal">{initials(match.teamB.name)}</span>
          <span className="spacer" />
          {live ? (
            <span className="ds-badge ds-badge--success"><i className="ds-dot" />Live</span>
          ) : (
            <span className="ds-badge">{match.ruleSet.name}</span>
          )}
        </div>

        {state.innings.length === 0 ? (
          <div className="ds-caption" style={{ marginTop: "var(--ds-space-2)" }}>This match has not started yet.</div>
        ) : (
          state.innings.map((innings) => (
            <div className="ds-datarow" key={innings.inningsNo} style={{ marginTop: "var(--ds-space-2)" }}>
              <span className="ds-datarow__name">{teamName(innings.battingTeamId)}</span>
              <span className="ds-datarow__value">{formatScore(innings)}</span>
              <span className="ds-datarow__sub">({formatOvers(innings.overs)} ov)</span>
            </div>
          ))
        )}

        {state.result !== null && (
          <div style={{ fontWeight: 700, color: "var(--ds-success-600)", marginTop: "var(--ds-space-3)", paddingTop: "var(--ds-space-3)", borderTop: "1px solid var(--ds-border)" }}>
            {state.result.text}
          </div>
        )}
      </article>

      {/* Action row */}
      <div className="row" style={{ marginTop: "var(--ds-space-3)", gap: "var(--ds-space-2)", flexWrap: "wrap" }}>
        <ShareButton
          title={`${match.teamA.name} v ${match.teamB.name}`}
          url={`${window.location.origin}${path}`}
        />
        <a className="ds-btn ds-btn--secondary ds-btn--sm" href={path} target="_blank" rel="noreferrer">
          Public page
        </a>
        {onResume !== null ? (
          <button className="ds-btn ds-btn--accent ds-btn--sm" onClick={onResume}>
            Resume scoring
          </button>
        ) : onStartScoring !== null && live ? (
          <button
            className="ds-btn ds-btn--primary ds-btn--sm"
            disabled={startingMatch}
            onClick={() => onStartScoring(match.id)}
          >
            {startingMatch ? "Starting..." : "Score this match"}
          </button>
        ) : null}
      </div>

      {/* Who can find this match. Owner only; the link itself works either way. */}
      {canChangeVisibility ? (
        <div className="ds-card" style={{ padding: "var(--ds-space-4)", marginTop: "var(--ds-space-3)" }}>
          <label className="row" style={{ gap: "var(--ds-space-3)", cursor: "pointer" }}>
            <span style={{ flex: 1 }}>
              <b style={{ display: "block" }}>Show in Live and Recent</b>
              <span className="ds-caption">
                {isPublic
                  ? "Everyone can find this match on their home screen."
                  : "Hidden from other people's home screens. Anyone with the link can still open it."}
              </span>
            </span>
            <input
              type="checkbox"
              role="switch"
              className="ds-switch"
              aria-label="Show in Live and Recent"
              aria-checked={isPublic}
              checked={isPublic}
              disabled={savingVisibility}
              onChange={(e) => void changeVisibility(e.target.checked)}
            />
          </label>
          {visibilityError !== null && (
            <div className="ds-hint ds-hint--error" style={{ marginTop: "var(--ds-space-2)" }}>{visibilityError}</div>
          )}
        </div>
      ) : null}

      {/* Squad — one card per team, same layout as match creation */}
      {live && !squadsReady && onResume === null ? (
        <>
          {[
            { team: match.teamA, count: teamACount, short: teamAShort, index: 0 as const },
            { team: match.teamB, count: teamBCount, short: teamBShort, index: 1 as const },
          ].filter((t) => t.short).map(({ team, count, index }) => {
            const needed = required - count;
            const squad = team.squad as readonly Player[];
            return (
              <article
                key={team.id}
                className="ds-card"
                style={{ padding: "var(--ds-space-4) var(--ds-space-5)", marginTop: "var(--ds-space-3)" }}
              >
                <div style={{ display: "flex", alignItems: "center", marginBottom: "var(--ds-space-3)" }}>
                  <span className={`ds-avatar ds-avatar--sm ${index === 0 ? "ds-avatar--amber" : "ds-avatar--teal"}`} style={{ marginRight: "var(--ds-space-2)" }}>
                    {initials(team.name)}
                  </span>
                  <span style={{ flex: 1, fontWeight: 700, fontSize: "var(--ds-text-md)" }}>{team.name}</span>
                  <span className="ds-badge ds-badge--warning">{count}/{required} players</span>
                </div>

                {/* Existing players as chips */}
                {squad.length > 0 ? (
                  <div style={{ display: "flex", flexWrap: "wrap", gap: "var(--ds-space-2)", marginBottom: "var(--ds-space-3)" }}>
                    {squad.map((p) => (
                      <span key={p.id} className="ds-chip" aria-pressed="true">{p.displayName}</span>
                    ))}
                  </div>
                ) : null}

                <button
                  className="ds-btn ds-btn--primary ds-btn--block"
                  style={{ marginTop: "var(--ds-space-2)" }}
                  onClick={() => setAddPlayerForTeam({ id: team.id, name: team.name, index })}
                >
                  <span className="ds-btn__icon">
                    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                      <path d="M12 5v14M5 12h14" />
                    </svg>
                  </span>
                  Add player
                </button>

                <p className="ds-hint" style={{ marginTop: "var(--ds-space-3)" }}>
                  {needed} more player{needed !== 1 ? "s" : ""} needed to start scoring
                </p>
              </article>
            );
          })}
        </>
      ) : null}

      {/* Toss */}
      {showToss && onStartScoring !== null ? (
        <div className="ds-card" style={{ marginTop: "var(--ds-space-3)", padding: "var(--ds-space-4)" }}>
          <div className="ds-title" style={{ marginBottom: "var(--ds-space-3)" }}>Toss</div>

          <div className="row" style={{ marginBottom: "var(--ds-space-3)", gap: "var(--ds-space-3)" }}>
            <button className="ds-btn ds-btn--secondary ds-btn--sm" onClick={flipCoin} disabled={coin?.spinning === true}>
              {coin === null ? "Flip a coin" : "Flip again"}
            </button>
            {coin === null ? (
              <span className="ds-caption">Or just pick the winner below.</span>
            ) : (
              <>
                <span
                  className="ds-avatar ds-avatar--violet"
                  aria-hidden="true"
                  style={coin.spinning ? { animation: "spin 0.6s linear infinite" } : undefined}
                >
                  {coin.spinning ? "?" : coinInitials(coin.face === "heads" ? match.teamA.name : match.teamB.name)}
                </span>
                <span className="ds-caption" role="status">
                  {coin.spinning
                    ? "Flipping..."
                    : `${coin.face === "heads" ? match.teamA.name : match.teamB.name} won the toss`}
                </span>
              </>
            )}
          </div>

          <div className="ds-options" style={{ marginBottom: "var(--ds-space-2)" }}>
            {[match.teamA, match.teamB].map((team) => (
              <button
                key={team.id}
                className="ds-option"
                aria-pressed={tossWinner === team.id}
                onClick={() => setTossWinner(team.id)}
              >
                <span className="ds-option__value">{team.name}</span>
                <span className="ds-option__label">won the toss</span>
              </button>
            ))}
          </div>
          <div className="ds-options" style={{ marginBottom: "var(--ds-space-4)" }}>
            {(["bat", "bowl"] as const).map((d) => (
              <button
                key={d}
                className="ds-option"
                aria-pressed={tossDecision === d}
                onClick={() => setTossDecision(d)}
              >
                <span className="ds-option__value">chose to {d}</span>
              </button>
            ))}
          </div>

          <button
            className="ds-btn ds-btn--primary ds-btn--block"
            disabled={startingMatch}
            onClick={() => {
              setStartingMatch(true);
              void (async () => {
                try {
                  onStartScoring(match.id, { winner: tossWinner, decision: tossDecision });
                } catch {
                  setStartingMatch(false);
                }
              })();
            }}
          >
            {startingMatch ? "Starting..." : "Start scoring"}
          </button>
        </div>
      ) : null}

      {/* AI Summary */}
      {state.result !== null ? (
        <AISummaryCard state={state} name={name} teamName={teamName} />
      ) : null}

      {/* Notification subscribe */}
      <NotifyButton matchId={match.id} />

      {/* Scorecard — open by default on this screen */}
      <Scorecard state={state} name={name} teamName={teamName} open showStyle />

      {/* Only when shots were recorded. A match scored without the wheel has nothing to add here. */}
      <WagonWheelCard state={state} name={name} teamName={teamName} />

      {state.innings.map((innings) => (
        <InningsDetail key={innings.inningsNo} innings={innings} name={name} teamName={teamName} />
      ))}

      {addPlayerForTeam !== null && (
        <AddPlayerSheet
          teamId={addPlayerForTeam.id}
          teamName={addPlayerForTeam.name}
          existingPlayerIds={
            (addPlayerForTeam.index === 0
              ? match.teamA.squad as readonly Player[]
              : match.teamB.squad as readonly Player[]
            ).map((p) => p.id)
          }
          existingNames={
            (addPlayerForTeam.index === 0
              ? match.teamA.squad as readonly Player[]
              : match.teamB.squad as readonly Player[]
            ).map((p) => p.displayName)
          }
          onPlayerAdded={() => { onRefresh(); setAddPlayerForTeam(null); }}
          onClose={() => setAddPlayerForTeam(null)}
        />
      )}
    </>
  );
}

/** The two things the shared `Scorecard` does not carry: partnerships and the over-by-over. */
function InningsDetail({
  innings,
  name,
  teamName,
}: {
  readonly innings: InningsState;
  readonly name: (playerId: PlayerId) => string;
  readonly teamName: (teamId: TeamId) => string;
}): React.ReactElement | null {
  if (innings.deliveries.length === 0 && innings.partnerships.length === 0) return null;

  const overs = [...new Set(innings.deliveries.map((entry) => entry.over))].sort((a, b) => a - b);

  return (
    <div className="ds-card" style={{ marginTop: "var(--ds-space-3)", padding: "var(--ds-space-4)" }}>
      <div className="ds-title" style={{ marginBottom: "var(--ds-space-3)" }}>
        {teamName(innings.battingTeamId)} — over by over
      </div>

      {innings.partnerships.length === 0 ? null : (
        <div style={{ marginBottom: "var(--ds-space-3)" }}>
          <div className="ds-label" style={{ marginBottom: "var(--ds-space-2)" }}>Partnerships</div>
          {innings.partnerships.map((stand, index) => (
            <div className="ds-caption" key={`${stand.playerAId}-${stand.playerBId}-${index}`} style={{ marginBottom: "var(--ds-space-1)" }}>
              {name(stand.playerAId)} &amp; {name(stand.playerBId)} — <b>{stand.runs}</b> ({stand.balls})
              {stand.unbroken ? " · unbroken" : ""}
            </div>
          ))}
        </div>
      )}

      <div className="stack">
        {overs.map((overNo) => (
          <OverStrip
            key={overNo}
            balls={innings.deliveries.filter((entry) => entry.over === overNo)}
            overNo={overNo}
          />
        ))}
      </div>
    </div>
  );
}

function ShareButton({
  title,
  url,
}: {
  readonly title: string;
  readonly url: string;
}): React.ReactElement {
  const [copied, setCopied] = useState(false);
  const share = async (): Promise<void> => {
    try {
      if (typeof navigator.share === "function") {
        await navigator.share({ title, url });
        return;
      }
      await navigator.clipboard.writeText(url);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      // A cancelled sheet rejects, and so does a clipboard write without focus. Neither is
      // worth an error: the public page button beside this one is the fallback.
    }
  };
  return (
    <button className="ds-btn ds-btn--secondary ds-btn--sm" onClick={() => void share()}>
      {copied ? "Link copied" : "Share link"}
    </button>
  );
}

function AISummaryCard({
  state,
  name,
  teamName,
}: {
  readonly state: MatchState;
  readonly name: (playerId: PlayerId) => string;
  readonly teamName: (teamId: TeamId) => string;
}): React.ReactElement {
  const lastInnings = state.innings[state.innings.length - 1];
  const topScorer = lastInnings
    ? [...state.innings.flatMap((inn) => inn.batting)]
        .filter((b) => b.batted)
        .sort((a, b) => b.runs - a.runs)[0]
    : null;
  const topBowler = lastInnings
    ? [...state.innings.flatMap((inn) => inn.bowling)]
        .filter((b) => b.wickets > 0)
        .sort((a, b) => b.wickets - a.wickets)[0]
    : null;

  const summary = generateMatchSummary({
    winnerName: state.result?.winnerTeamId
      ? teamName(state.result.winnerTeamId)
      : null,
    resultText: state.result?.text ?? "Match completed",
    topScorer: topScorer
      ? {
          name: name(topScorer.playerId),
          runs: topScorer.runs,
          balls: topScorer.balls,
          notOut: topScorer.dismissal === null,
        }
      : null,
    topWicketTaker: topBowler
      ? {
          name: name(topBowler.playerId),
          wickets: topBowler.wickets,
          runs: topBowler.runsConceded,
        }
      : null,
  });

  return (
    <div className="ds-card" style={{
      marginTop: "var(--ds-space-3)",
      padding: "var(--ds-space-4)",
      borderLeft: "3px solid var(--ds-accent)",
    }}>
      <div className="ds-label" style={{ color: "var(--ds-accent)", marginBottom: "var(--ds-space-2)" }}>
        AI Summary
      </div>
      <div style={{ fontSize: "var(--ds-text-sm)", lineHeight: "var(--ds-leading-normal)" }}>{summary}</div>
    </div>
  );
}

/**
 * Wicket and result notifications for one match.
 *
 * ## Why this looks more careful than a toggle needs to
 *
 * An earlier version of this button shipped and never worked, silently, in two independent
 * ways — it awaited a promise that never settles, and it subscribed without the application
 * server key Chrome requires. Both are fixed in `notifications.ts`; what is fixed *here* is
 * the third problem, which is that it had no way to say so. Every outcome now puts something
 * on screen.
 *
 * ## The permission is asked once, ever
 *
 * `Notification.requestPermission` cannot be re-asked after a denial, and on iOS the only way
 * back is deleting the app. So: no prompt on load, no prompt on render, and the reason is on
 * screen before the tap that triggers it. `canOffer()` keeps the control off the screen
 * entirely where it could not work — a browser with no push support, or one where permission
 * is already denied — because a control that exists only to fail is worse than its absence.
 */
function NotifyButton({ matchId }: { readonly matchId: string }): React.ReactElement | null {
  // Seeded from what this device recorded when it subscribed, so a reload does not silently
  // claim the scorer is not following a match they are. The browser cannot answer this itself
  // — `pushManager` knows only that this origin has one subscription, not which matches it
  // covers — so a per-device note is the honest source. See `notifications.ts`.
  const [subscribed, setSubscribed] = useState(() => isSubscribedHere(matchId));
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  // Read once on mount rather than on every render: `Notification.permission` can change
  // under us, and a control that vanishes mid-tap is worse than one that is briefly stale.
  const [offerable] = useState(() => canOffer());
  if (!offerable) return null;

  const toggle = async (): Promise<void> => {
    setBusy(true);
    setMessage(null);
    try {
      if (subscribed) {
        const ok = await unsubscribe(matchId);
        setSubscribed(!ok);
        if (!ok) setMessage("Could not turn these off. Try again in a moment.");
        return;
      }

      const outcome = await subscribe(matchId);
      setSubscribed(outcome === "subscribed");
      // Named outcomes rather than a boolean, because "you said no" and "this did not work"
      // want different words — and telling someone to retry a thing they deliberately declined
      // is its own small insult.
      if (outcome === "denied") {
        setMessage("Notifications are blocked for this site in your browser settings.");
      } else if (outcome === "unavailable") {
        setMessage("This browser cannot show match notifications.");
      } else if (outcome === "failed") {
        setMessage("Could not turn these on. Try again in a moment.");
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ marginTop: "var(--ds-space-3)" }}>
      <button
        className={`ds-btn ds-btn--sm ${subscribed ? "ds-btn--accent" : "ds-btn--secondary"}`}
        onClick={() => void toggle()}
        disabled={busy}
        type="button"
      >
        {busy ? "Just a moment…" : subscribed ? "Notifications on" : "Tell me about wickets"}
      </button>
      {/* The reason, before the permission prompt rather than after it. */}
      {!subscribed && message === null && (
        <span className="ds-caption" style={{ display: "block", marginTop: "var(--ds-space-2)" }}>
          A notification when a wicket falls and when the match ends. Nothing else.
        </span>
      )}
      {message !== null && (
        <span
          className="ds-caption"
          role="status"
          style={{ display: "block", marginTop: "var(--ds-space-2)", color: "var(--ds-warning-600)" }}
        >
          {message}
        </span>
      )}
    </div>
  );
}
